import { useEffect, useState, type FormEvent } from "react";
import AdminLayout from "../AdminLayout";
import { API_BASE } from "@/config/api";
import { uploadMedia } from "../uploadMedia";
import { normalizeMediaUrl } from "@/lib/mediaUrl";

interface MediaRow {
  id: number;
  filename: string;
  url: string;
  alt_text: string;
  created_at: string;
}

export default function AdminMedia() {
  const [items, setItems] = useState<MediaRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState<File | null>(null);
  const [altText, setAltText] = useState("");
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);

  async function load() {
    setLoading(true);
    const res = await fetch(`${API_BASE}/media`);
    setItems(await res.json());
    setLoading(false);
  }

  useEffect(() => {
    load();
  }, []);

  async function handleUpload(e: FormEvent) {
    e.preventDefault();
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      await uploadMedia(file, altText);
      setFile(null);
      setAltText("");
      (document.getElementById("media-file") as HTMLInputElement | null)?.value &&
        ((document.getElementById("media-file") as HTMLInputElement).value = "");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error de conexión.");
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete(id: number) {
    if (!confirm("¿Eliminar esta imagen?")) return;
    await fetch(`${API_BASE}/media/${id}`, { method: "DELETE" });
    await load();
  }

  function copyUrl(url: string) {
    navigator.clipboard?.writeText(url);
  }

  return (
    <AdminLayout>
      <h1 className="font-heading font-bold text-2xl text-foreground-950">Medios</h1>
      <p className="mt-1 text-sm text-foreground-500">
        Sube imágenes acá y copia su URL para usarla donde la necesites.
      </p>

      <form onSubmit={handleUpload} className="mt-6 rounded-lg border border-background-200 bg-background-50 p-6 space-y-4">
        {error && (
          <div className="rounded-md bg-accent-100 border border-accent-200 p-3 text-sm text-accent-800">{error}</div>
        )}
        <div>
          <label htmlFor="media-file" className="block text-sm font-medium text-foreground-700 mb-1.5">
            Archivo (JPG, PNG, WEBP, GIF o SVG, máx. 8MB)
          </label>
          <input
            id="media-file"
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="block w-full text-sm text-foreground-700"
          />
        </div>
        <div>
          <label htmlFor="media-alt" className="block text-sm font-medium text-foreground-700 mb-1.5">
            Texto alternativo (descripción de la imagen)
          </label>
          <input
            id="media-alt"
            type="text"
            value={altText}
            onChange={(e) => setAltText(e.target.value)}
            className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
          />
        </div>
        <button
          type="submit"
          disabled={!file || uploading}
          className="rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 transition disabled:opacity-60 cursor-pointer"
        >
          {uploading ? "Subiendo..." : "Subir imagen"}
        </button>
      </form>

      <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-4">
        {loading ? (
          <p className="text-sm text-foreground-500">Cargando...</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-foreground-500">Aún no hay imágenes subidas.</p>
        ) : (
          items.map((m) => (
            <div key={m.id} className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
              <img src={normalizeMediaUrl(m.url)} alt={m.alt_text} className="w-full h-32 object-cover" />
              <div className="p-3">
                <p className="text-xs text-foreground-500 truncate" title={m.alt_text}>
                  {m.alt_text || "(sin descripción)"}
                </p>
                <div className="mt-2 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => copyUrl(m.url)}
                    className="text-xs text-primary-600 hover:text-primary-700 cursor-pointer"
                  >
                    Copiar URL
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(m.id)}
                    className="text-xs text-accent-600 hover:text-accent-700 cursor-pointer"
                  >
                    Eliminar
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </AdminLayout>
  );
}
